import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AlarmsWidget — a tile-style widget the owner can pin to the canvas to
 * see / add / toggle / delete alarms without using voice.
 *
 * Owner asked: "I want an actual mini alarm widget and I set whatever time
 * I want it to go off on".
 *
 * Each alarm has hh:mm, label, repeat (once/daily/weekdays/weekends),
 * enabled toggle. The backend `alarm_tick_job` checks every minute and
 * fires the matching one — AlarmRinger handles the screen takeover.
 *
 * The owner's local timezone offset (in hours) is sent on create so the
 * server can compare against the owner's wall clock, not UTC.
 */
const REPEATS = [
  { v: "once", l: "Once" },
  { v: "daily", l: "Daily" },
  { v: "weekdays", l: "Mon-Fri" },
  { v: "weekends", l: "Sat-Sun" },
];

export default function AlarmsWidget() {
  const [alarms, setAlarms] = useState([]);
  const [hhmm, setHhmm] = useState("07:00");
  const [label, setLabel] = useState("Wake up");
  const [repeat, setRepeat] = useState("daily");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/jarvis/alarms");
      setAlarms(r.data?.alarms || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  const add = async () => {
    if (busy) return;
    if (!/^\d{2}:\d{2}$/.test(hhmm)) return;
    setBusy(true);
    try {
      // Send the owner's local timezone offset so the server fires at the
      // right local wall-clock time (not UTC).
      const tz_offset_hours = -new Date().getTimezoneOffset() / 60;
      await api.post("/jarvis/alarms", { hhmm, label: label.trim() || "Wake up", repeat, tz_offset_hours });
      setLabel("Wake up");
      await load();
    } finally { setBusy(false); }
  };

  const toggle = async (a) => {
    await api.patch(`/jarvis/alarms/${a.id}`, { enabled: !a.enabled });
    await load();
  };

  const del = async (a) => {
    if (!window.confirm(`Delete the ${a.hhmm} alarm "${a.label}"?`)) return;
    await api.delete(`/jarvis/alarms/${a.id}`);
    await load();
  };

  return (
    <div className="rc-panel p-3" data-testid="alarms-widget" style={{ minWidth: 280 }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="rc-label">⏰ ALARMS</div>
          <div className="text-[10px] opacity-60">Wakes the room — speakers ring + plug on</div>
        </div>
        <div className="text-[10px] opacity-60" data-testid="alarms-count">{alarms.length}</div>
      </div>

      {/* Add new */}
      <div className="border border-[var(--rc-gold-dim)] p-2 mb-3 space-y-2">
        <div className="flex gap-2">
          <input
            type="time"
            value={hhmm}
            onChange={(e) => setHhmm(e.target.value)}
            data-testid="alarms-new-time"
            className="bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-sm font-mono w-28"
          />
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label"
            data-testid="alarms-new-label"
            className="flex-1 bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-xs"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            data-testid="alarms-new-repeat"
            className="bg-black border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1 text-[10px] flex-1"
          >
            {REPEATS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
          <button
            onClick={add}
            disabled={busy}
            data-testid="alarms-new-add"
            className="rc-btn !text-[10px] !py-1 !px-3"
          >+ ADD</button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {alarms.length === 0 && (
          <div className="text-[10px] opacity-50 text-center py-3">No alarms set. Add one above or say "set alarm for 7am".</div>
        )}
        {alarms.map((a) => (
          <div
            key={a.id}
            data-testid={`alarm-row-${a.id}`}
            className={`flex items-center gap-2 border px-2 py-1.5 ${a.enabled ? "border-[var(--rc-gold-dim)]" : "border-[var(--rc-text-dim)] opacity-50"}`}
          >
            <div className="font-mono text-base" style={{ color: a.enabled ? "var(--rc-gold)" : "var(--rc-text-dim)" }}>
              {a.hhmm}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs truncate">{a.label}</div>
              <div className="text-[9px] opacity-60 uppercase">{a.repeat}</div>
            </div>
            <button
              onClick={() => toggle(a)}
              data-testid={`alarm-toggle-${a.id}`}
              className="text-[9px] tracking-widest border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] px-2 py-1"
              title={a.enabled ? "Disable" : "Enable"}
            >{a.enabled ? "ON" : "OFF"}</button>
            <button
              onClick={() => del(a)}
              data-testid={`alarm-del-${a.id}`}
              className="text-[var(--rc-text-dim)] hover:text-red-400 text-xs"
              title="Delete"
            >✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}
